import { useState } from 'react';

import { fmt } from '@dcc-erp/shared';

import { useTerminatePrepaidContract } from '../../lib/queries';
import { AppModal } from '../AppModal';
import { todayISO } from './pp-helpers';

import type { PPItem } from './pp-helpers';

// ============================================================
// Lifecycle modals (P6) — terminate+write-off, modify/re-spread, suspend.
// Each acts on the selected contract's apiId via the lifecycle hooks.
// ============================================================

export function PPTerminateModal({
  item,
  companyId,
  onClose,
}: {
  item: PPItem;
  companyId: string | undefined;
  onClose: () => void;
}) {
  const mut = useTerminatePrepaidContract(companyId);
  const [effectiveDate, setEffectiveDate] = useState(todayISO);
  const [reason, setReason] = useState('');
  const [refund, setRefund] = useState('');
  const [refundAccount, setRefundAccount] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const remaining = item.monthly * item.remain;

  function submit() {
    if (!item.apiId) return;
    setErr(null);
    mut.mutate(
      {
        id: item.apiId,
        data: {
          effectiveDate,
          reason,
          ...(refund ? { refundAmount: Number(refund) } : {}),
          ...(refundAccount ? { refundAccountCode: refundAccount } : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e) => setErr(e instanceof Error ? e.message : 'ยกเลิกไม่สำเร็จ'),
      },
    );
  }

  return (
    <AppModal
      title={`ยกเลิกสัญญา — ${item.id}`}
      subtitle="ตัดจำหน่ายยอดคงเหลือเป็นค่าใช้จ่าย (และคืนเงินถ้ามี)"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            style={{ background: 'var(--negative)' }}
            disabled={mut.isPending || !reason.trim()}
            onClick={submit}
          >
            {mut.isPending ? 'กำลังยกเลิก…' : 'ยืนยันยกเลิกสัญญา'}
          </button>
        </>
      }
    >
      <div className="meta" style={{ marginBottom: 10 }}>
        ยอดคงเหลือโดยประมาณ <b>{fmt(remaining)}</b> บาท จะถูกตัดเป็นค่าใช้จ่าย (หักส่วนที่คืนเงิน)
      </div>
      <label className="meta">วันที่มีผล (งวดต้องเปิดอยู่)</label>
      <input
        className="input"
        type="date"
        value={effectiveDate}
        onChange={(e) => setEffectiveDate(e.target.value)}
        style={{ width: '100%', marginBottom: 10 }}
      />
      <label className="meta">เหตุผล *</label>
      <textarea
        className="input"
        rows={2}
        value={reason}
        placeholder="เช่น ยกเลิกโครงการ / คืนกรมธรรม์"
        onChange={(e) => setReason(e.target.value)}
        style={{ width: '100%', resize: 'vertical', padding: 8, marginBottom: 10 }}
      />
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label className="meta">คืนเงิน (ถ้ามี)</label>
          <input
            className="input"
            type="number"
            value={refund}
            placeholder="0.00"
            onChange={(e) => setRefund(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label className="meta">บัญชีรับเงินคืน</label>
          <input
            className="input"
            value={refundAccount}
            placeholder="ค่าเริ่มต้น: ธนาคารหลัก"
            onChange={(e) => setRefundAccount(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>
      {err && <div style={{ color: 'var(--negative)', fontSize: 14, marginTop: 8 }}>{err}</div>}
    </AppModal>
  );
}
